import { Slot } from '@radix-ui/react-slot';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import {
    type ButtonHTMLAttributes,
    type CSSProperties,
    createContext,
    type HTMLAttributes,
    type ReactNode,
    type TransitionEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Button } from './button.js';
import { cn } from '../../utils.js';

const sidebarTransition = 'duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:duration-0';

type SidebarState = 'expanded' | 'collapsed';
type SidebarVisualState = 'expanded' | 'collapsed' | 'expanding' | 'collapsing';

interface SidebarContextValue {
    open: boolean;
    state: SidebarState;
    visualState: SidebarVisualState;
    isCollapsedLayout: boolean;
    isTextVisible: boolean;
    setOpen: (open: boolean) => void;
    toggleSidebar: () => void;
    completeTransition: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export interface SidebarProviderProps extends HTMLAttributes<HTMLDivElement> {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    children: ReactNode;
}

export function SidebarProvider({
    defaultOpen = true,
    open,
    onOpenChange,
    children,
    className,
    style,
    ...props
}: SidebarProviderProps) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const currentOpen = open ?? uncontrolledOpen;
    const [visualState, setVisualState] = useState<SidebarVisualState>(currentOpen ? 'expanded' : 'collapsed');

    const setOpen = useCallback((nextOpen: boolean) => {
        setUncontrolledOpen(nextOpen);
        onOpenChange?.(nextOpen);
    }, [onOpenChange]);

    const toggleSidebar = useCallback(() => {
        setOpen(!currentOpen);
    }, [currentOpen, setOpen]);

    useEffect(() => {
        setVisualState((currentVisualState) => {
            if (currentOpen) return currentVisualState === 'expanded' ? 'expanded' : 'expanding';
            return currentVisualState === 'collapsed' ? 'collapsed' : 'collapsing';
        });
    }, [currentOpen]);

    useEffect(() => {
        if (visualState === 'expanded' || visualState === 'collapsed') return;
        const timeoutId = globalThis.setTimeout(() => {
            setVisualState(currentOpen ? 'expanded' : 'collapsed');
        }, 320);
        return () => globalThis.clearTimeout(timeoutId);
    }, [currentOpen, visualState]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key.toLowerCase() !== 'b') return;
            if (!event.metaKey && !event.ctrlKey) return;
            event.preventDefault();
            toggleSidebar();
        };

        globalThis.addEventListener('keydown', handleKeyDown);
        return () => globalThis.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebar]);

    const value = useMemo<SidebarContextValue>(() => ({
        open: currentOpen,
        state: currentOpen ? 'expanded' : 'collapsed',
        visualState,
        isCollapsedLayout: visualState === 'collapsed',
        isTextVisible: visualState === 'expanded',
        setOpen,
        toggleSidebar,
        completeTransition: () => setVisualState(currentOpen ? 'expanded' : 'collapsed'),
    }), [currentOpen, setOpen, toggleSidebar, visualState]);

    return (
        <SidebarContext.Provider value={value}>
            <div
                data-sidebar-provider
                data-state={value.state}
                data-visual-state={value.visualState}
                data-collapsible={value.state === 'collapsed' ? 'icon' : ''}
                className={cn('group/sidebar min-h-dvh bg-background text-foreground', className)}
                style={{
                    '--sidebar-width': '17rem',
                    '--sidebar-width-icon': '3.5rem',
                    '--app-header-height': '3.5rem',
                    ...style,
                } as CSSProperties}
                {...props}
            >
                {children}
            </div>
        </SidebarContext.Provider>
    );
}

export function useSidebar() {
    const context = useContext(SidebarContext);
    if (!context) throw new Error('useSidebar must be used within SidebarProvider');
    return context;
}

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
    side?: 'left' | 'right';
    collapsible?: 'icon' | 'none';
}

export function Sidebar({
    side = 'left',
    collapsible = 'icon',
    className,
    children,
    style,
    ...props
}: SidebarProps) {
    const { open, visualState, completeTransition } = useSidebar();
    const sidebarWidth = open || collapsible === 'none' ? 'var(--sidebar-width)' : 'var(--sidebar-width-icon)';

    const handleTransitionEnd = (event: TransitionEvent<HTMLElement>) => {
        props.onTransitionEnd?.(event);
        if (event.target !== event.currentTarget) return;
        if (event.propertyName !== 'width' && event.propertyName !== 'flex-basis') return;
        completeTransition();
    };

    return (
        <aside
            data-sidebar
            data-side={side}
            data-collapsible={collapsible}
            data-visual-state={visualState}
            className={cn(
                'group/sidebar-panel sticky top-[var(--app-header-height)] hidden h-[calc(100dvh-var(--app-header-height))] shrink-0 flex-col border-border bg-background-subtle transition-[width,flex-basis] lg:flex data-[side=left]:border-r data-[side=right]:border-l',
                sidebarTransition,
                className,
            )}
            style={{ width: sidebarWidth, flexBasis: sidebarWidth, ...style }}
            {...props}
            onTransitionEnd={handleTransitionEnd}
        >
            {children}
        </aside>
    );
}

export function SidebarHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    const { isCollapsedLayout } = useSidebar();

    return (
        <div
            data-sidebar-header
            className={cn(
                'shrink-0 border-b border-border py-3 transition-[padding]',
                sidebarTransition,
                isCollapsedLayout ? 'px-2' : 'px-3',
                className,
            )}
            {...props}
        />
    );
}

export function SidebarContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            data-sidebar-content
            className={cn(
                'min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 [scrollbar-gutter:stable] [scrollbar-width:thin] group-data-[collapsible=icon]/sidebar:[scrollbar-width:none] group-data-[collapsible=icon]/sidebar:[&::-webkit-scrollbar]:hidden',
                className,
            )}
            {...props}
        />
    );
}

export function SidebarFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    const { isCollapsedLayout } = useSidebar();

    return (
        <div
            data-sidebar-footer
            className={cn(
                'shrink-0 border-t border-border py-3 transition-[padding]',
                sidebarTransition,
                isCollapsedLayout ? 'px-2' : 'px-3',
                className,
            )}
            {...props}
        />
    );
}

export function SidebarInset({ className, ...props }: HTMLAttributes<HTMLElement>) {
    return <main data-sidebar-inset className={cn('min-w-0 flex-1', className)} {...props} />;
}

export function SidebarGroup({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    return <div data-sidebar-group className={cn('grid min-w-0 gap-1 transition-[gap]', sidebarTransition, className)} {...props} />;
}

export function SidebarGroupLabel({ className, style, ...props }: HTMLAttributes<HTMLHeadingElement>) {
    const { isTextVisible } = useSidebar();
    const labelStyle = {
        fontSize: '1rem',
        lineHeight: 1.2,
        transform: 'scale(0.85)',
        transformOrigin: 'left center',
        ...style,
    } satisfies CSSProperties;

    return (
        <h2
            data-sidebar-group-label
            data-visible={isTextVisible ? 'true' : 'false'}
            aria-hidden={!isTextVisible}
            className={cn(
                'overflow-hidden px-2 pb-1 font-normal uppercase text-foreground-subtle transition-[max-height,opacity,padding]',
                sidebarTransition,
                isTextVisible ? 'max-h-5 opacity-100' : 'max-h-0 py-0 opacity-0',
                className,
            )}
            style={labelStyle}
            {...props}
        />
    );
}

export function SidebarGroupContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    return <div data-sidebar-group-content className={cn('grid min-w-0 gap-1', className)} {...props} />;
}

export function SidebarMenu({ className, ...props }: HTMLAttributes<HTMLUListElement>) {
    return <ul data-sidebar-menu className={cn('grid min-w-0 gap-1', className)} {...props} />;
}

export function SidebarMenuItem({ className, ...props }: HTMLAttributes<HTMLLIElement>) {
    return <li data-sidebar-menu-item className={cn('group/menu-item relative min-w-0', className)} {...props} />;
}

export interface SidebarMenuButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    asChild?: boolean;
    isActive?: boolean;
    disabled?: boolean;
    tooltip?: string;
    size?: 'default' | 'sm' | 'lg';
    variant?: 'default' | 'outline';
}

export function SidebarMenuButton({
    asChild = false,
    isActive = false,
    disabled = false,
    tooltip,
    size = 'default',
    variant = 'default',
    className,
    children,
    ...props
}: SidebarMenuButtonProps) {
    const { isCollapsedLayout } = useSidebar();
    const Comp = asChild ? Slot : 'button';
    const sizeClasses = {
        default: 'min-h-9 py-1.5 text-sm',
        sm: 'min-h-8 py-1.5 text-xs',
        lg: 'min-h-12 py-2.5 text-sm',
    }[size];
    const variantClasses = variant === 'outline'
        ? 'border border-border hover:border-primary/40 hover:bg-primary/5'
        : 'hover:bg-primary/5 hover:text-primary';

    return (
        <Comp
            data-sidebar-menu-button
            data-active={isActive || undefined}
            data-disabled={disabled || undefined}
            data-size={size}
            data-variant={variant}
            type={asChild ? undefined : 'button'}
            disabled={asChild ? undefined : disabled}
            title={tooltip ?? props.title}
            aria-label={isCollapsedLayout && tooltip ? tooltip : props['aria-label']}
            className={cn(
                'peer/menu-button grid w-full min-w-0 grid-cols-[2.5rem_minmax(0,1fr)_1.75rem] items-center rounded-xl pr-2 text-left font-medium outline-none transition-[background-color,border-color,color,box-shadow] focus-visible:ring-2 focus-visible:ring-primary/30 data-[active=true]:bg-primary/10 data-[active=true]:text-primary data-[active=true]:hover:bg-primary/10 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-55',
                sidebarTransition,
                sizeClasses,
                variantClasses,
                className,
            )}
            {...props}
        >
            {children}
        </Comp>
    );
}

export function SidebarMenuIcon({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
    return <span data-sidebar-menu-icon className={cn('flex size-9 shrink-0 items-center justify-center justify-self-start', className)} {...props} />;
}

export function SidebarMenuLabel({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
    const { isTextVisible } = useSidebar();

    return (
        <span
            data-sidebar-menu-label
            data-visible={isTextVisible ? 'true' : 'false'}
            aria-hidden={!isTextVisible}
            className={cn(
                'min-w-0 flex-1 truncate whitespace-nowrap transition-[max-width,opacity]',
                sidebarTransition,
                isTextVisible ? 'max-w-48 opacity-100' : 'max-w-0 opacity-0',
                className,
            )}
            {...props}
        />
    );
}

export function SidebarMenuBadge({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
    const { isTextVisible } = useSidebar();

    return (
        <span
            data-sidebar-menu-badge
            data-visible={isTextVisible ? 'true' : 'false'}
            aria-hidden={!isTextVisible}
            className={cn(
                'ml-auto inline-flex min-h-5 items-center overflow-hidden whitespace-nowrap rounded-full border border-border px-1.5 text-[0.6875rem] font-semibold text-foreground-subtle transition-[max-width,opacity]',
                sidebarTransition,
                isTextVisible ? 'max-w-24 opacity-100' : 'max-w-0 opacity-0',
                className,
            )}
            {...props}
        />
    );
}

export function SidebarTrigger({ className, ...props }: HTMLAttributes<HTMLButtonElement>) {
    const { open, toggleSidebar } = useSidebar();

    return (
        <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
            className={className}
            onClick={toggleSidebar}
            {...props}
        >
            {open ? <PanelLeftClose className="size-4" /> : <PanelLeftOpen className="size-4" />}
        </Button>
    );
}

export function SidebarRail({ className, ...props }: HTMLAttributes<HTMLButtonElement>) {
    const { toggleSidebar } = useSidebar();

    return (
        <button
            type="button"
            data-sidebar-rail
            aria-label="Toggle sidebar"
            className={cn('absolute inset-y-0 z-10 hidden w-6 cursor-pointer opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100 lg:block group-data-[side=left]/sidebar-panel:-right-3 group-data-[side=right]/sidebar-panel:-left-3', className)}
            onClick={toggleSidebar}
            {...props}
        />
    );
}
